/**
 * Home page portfolio grid: items, lazy loading, incremental loading.
 * Items loaded from kurtenj-web-images repo via jsDelivr (see portfolioFeed.js).
 */

import { portfolioItems, loadPortfolioFeed } from './data/portfolioFeed.js';
import { openModal } from './modal.js';
import {
  LAZY_LOAD_EAGER_COUNT,
  LAZY_LOAD_ROOT_MARGIN,
  PORTFOLIO_BATCH_SIZE,
} from './constants.js';

let visibleCount = 0;
let imageObserver = null;

function createPortfolioItem(item, index) {
  const itemDiv = document.createElement('div');
  itemDiv.className = 'portfolio-shot';
  itemDiv.dataset.index = index;
  itemDiv.classList.add('skeleton-loading');

  const img = document.createElement('img');
  img.alt = '';
  img.dataset.src = item.image;

  img.onload = () => itemDiv.classList.remove('skeleton-loading');
  img.onerror = () => itemDiv.classList.remove('skeleton-loading');

  itemDiv.style.cursor = 'pointer';
  itemDiv.appendChild(img);
  return itemDiv;
}

function getOrCreateObserver() {
  if (imageObserver) return imageObserver;

  imageObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const img = entry.target.querySelector('img');
        if (!img?.dataset.src) return;
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        if (img.complete && img.naturalWidth > 0) {
          entry.target.classList.remove('skeleton-loading');
        }
        imageObserver.unobserve(entry.target);
      });
    },
    { rootMargin: LAZY_LOAD_ROOT_MARGIN, threshold: 0.01 },
  );

  return imageObserver;
}

function observeShot(shot) {
  const img = shot.querySelector('img');
  if (!img?.dataset.src) return;

  if (visibleCount <= LAZY_LOAD_EAGER_COUNT) {
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    if (img.complete && img.naturalWidth > 0)
      shot.classList.remove('skeleton-loading');
  } else {
    getOrCreateObserver().observe(shot);
  }
}

/**
 * Render the next batch of portfolio items into the grid.
 * Returns the number of new items added.
 */
function renderNextBatch() {
  const portfolioGrid = document.getElementById('portfolioGrid');
  if (!portfolioGrid) return 0;

  const start = visibleCount;
  const end = Math.min(
    visibleCount + PORTFOLIO_BATCH_SIZE,
    portfolioItems.length,
  );
  const added = end - start;

  for (let i = start; i < end; i++) {
    const el = createPortfolioItem(portfolioItems[i], i);
    portfolioGrid.appendChild(el);
    observeShot(el);
  }

  visibleCount = end;

  updateActions();
  return added;
}

function updateActions() {
  const showMoreBtn = document.getElementById('showMoreBtn');
  if (!showMoreBtn) return;

  const allShown = visibleCount >= portfolioItems.length;
  showMoreBtn.style.display = allShown ? 'none' : '';
}

function setupActions() {
  const showMoreBtn = document.getElementById('showMoreBtn');
  const backToTopBtn = document.getElementById('backToTopBtn');

  if (showMoreBtn) {
    showMoreBtn.addEventListener('click', () => {
      renderNextBatch();
    });
  }

  if (backToTopBtn) {
    backToTopBtn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
}

function setupClickHandlers() {
  const portfolioGrid = document.getElementById('portfolioGrid');
  if (!portfolioGrid) return;

  portfolioGrid.addEventListener('click', (e) => {
    const shot = e.target.closest('.portfolio-shot');
    if (!shot) return;
    const img = shot.querySelector('img');
    const src = img?.src || img?.dataset?.src;
    const index = shot.dataset.index;
    if (src) openModal(src, index, shot);
  });

  portfolioGrid.addEventListener('animationend', (e) => {
    const shot = e.target.closest('.portfolio-shot');
    if (shot) shot.classList.add('animated');
  });
}

/**
 * Initialize home page portfolio: load feed, render first batch, set up actions.
 */
export function initializePortfolio() {
  const portfolioGrid = document.getElementById('portfolioGrid');
  if (!portfolioGrid) return;

  visibleCount = 0;

  portfolioGrid.setAttribute('aria-busy', 'true');
  loadPortfolioFeed().then(() => {
    portfolioGrid.removeAttribute('aria-busy');
    setupClickHandlers();
    renderNextBatch();
    setupActions();
  });
}
